import { Edit } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Grid,
  MenuItem,
  Chip as MuiChip,
  TablePagination,
  TextField,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import { ListFilterGroup } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { MichiganSOSBundleDto } from 'dtos'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { MichiganSOSBundlesParameters } from 'parameters/michiganSOSBundlesParameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'
import MichiganSOSBundlesAddEditDialog from './id/MichiganSOSBundlesAddEditDialog'
import MichiganSOSBundleListCard from './MichiganSOSBundlesListCard'

interface FilterChipProps {
  label: string
  onDelete: () => void
}

function FilterChip({ label, onDelete }: FilterChipProps) {
  return (
    <Grid item xs={12} sm='auto'>
      <MuiChip color='secondary' label={label} onDelete={onDelete} variant='outlined' />
    </Grid>
  )
}

const sessionStorageKey = 'michiganSoSBundles:parameters'

export default function MichiganSOSBundles() {
  const layout = useLayout()
  const navigate = useNavigate()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const defaultParameters: MichiganSOSBundlesParameters = {
    page: 0,
    pageSize: 10,
    orderBy: 'createdDate',
    // orderBy: 'michiganSOSBundleNumber',
    order: 'asc'
  }

  const [isCreatingMichiganSOSBundle, setIsCreatingMichiganSOSBundle] = useState<boolean>(false)
  const [isGettingMichiganSOSBundles, setIsGettingMichiganSOSBundles] = useState<boolean>(false)
  const [michiganSOSBundles, setMichiganSOSBundles] = useState<MichiganSOSBundleDto[]>([])
  const [michiganSOSBundle, setMichiganSOSBundle] = useState<MichiganSOSBundleDto>(new MichiganSOSBundleDto())
  const [michiganSOSBundleAddEditDialogOpen, setMichiganSOSBundleAddEditDialogOpen] = useState<boolean>(false)

  const [parameters, setParameters] = useState<MichiganSOSBundlesParameters>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultParameters
  )

  const [totalCount, setTotalCount] = useState<number>(0)

  const getMichiganSOSBundles = useDebounce((parameters: MichiganSOSBundlesParameters) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(parameters))

    setIsGettingMichiganSOSBundles(true)
    controllers.michiganSoSBundles
      .getMichiganSOSBundles(parameters)
      .then(response => {
        setMichiganSOSBundles(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingMichiganSOSBundles(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    getMichiganSOSBundles(parameters)
  }, [getMichiganSOSBundles, parameters])

  useEffect(() => {
    layout.setTitle('Michigan SoS Bundles')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingMichiganSOSBundles)
  }, [layout, isGettingMichiganSOSBundles])

  return (
    <>
      <MichiganSOSBundlesAddEditDialog
        initialValues={michiganSOSBundle}
        isLoading={isCreatingMichiganSOSBundle}
        onClose={() => {
          setMichiganSOSBundleAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsCreatingMichiganSOSBundle(true)
          controllers.michiganSoSBundles
            .createMichiganSOSBundle(values)
            .then(_ => {
              enqueueSnackbar('Michigan SoS Bundle Created Successfully!', { variant: 'success' })
              getMichiganSOSBundles(parameters)
              setMichiganSOSBundleAddEditDialogOpen(false)
            })
            .finally(() => {
              setIsCreatingMichiganSOSBundle(false)
            })
        }}
        open={michiganSOSBundleAddEditDialogOpen}
      />

      <Grid container spacing={1} alignItems='center'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Michigan SoS Bundles ({totalCount!})
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item xs={12} sm='auto' alignContent='center'>
                  <Grid item container xs={12}>
                    <ListFilterGroup>
                      <TextField
                        fullWidth
                        label='Filter Field'
                        margin='dense'
                        onChange={e => {
                          setFilterField(e.target.value)
                          setFilterValue('')
                        }}
                        select
                        size='small'
                        sx={{ minWidth: 200 }}
                        value={filterField || ''}
                      >
                        <MenuItem value='owner'>Owner</MenuItem>

                        <MenuItem value='vin'>Last 6 Of VIN</MenuItem>

                        <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                        <MenuItem value='year'>Year</MenuItem>

                        <MenuItem value='make'>Make</MenuItem>

                        <MenuItem value='model'>Model</MenuItem>
                      </TextField>

                      {['owner', 'vin', 'importerStockNumber', 'year', 'make', 'model'].includes(filterField) && (
                        <Grid container>
                          <Grid item xs={12} sm='auto'>
                            <TextField
                              disabled={!filterField}
                              label='Filter Value'
                              margin='dense'
                              onChange={e => {
                                setFilterValue(e.target.value)
                              }}
                              type={filterField === 'year' ? 'number' : 'text'}
                              size='small'
                              value={filterValue || ''}
                              inputProps={
                                filterField === 'vin'
                                  ? {
                                      maxLength: 6
                                    }
                                  : {}
                              }
                            />
                          </Grid>
                        </Grid>
                      )}

                      <Button
                        color='inherit'
                        disabled={
                          !filterField ||
                          !filterValue ||
                          (filterField === 'vehicleCheckInDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                        }
                        size='medium'
                        variant='contained'
                        onClick={() => {
                          if (filterField === 'vehicleCheckInDate') {
                            setParameters({
                              ...parameters,
                              vehicleCheckInDateFrom: filterValueFrom,
                              vehicleCheckInDateTo: filterValueTo
                            })
                          } else {
                            setParameters({
                              ...parameters,
                              [filterField]: filterValue
                            })
                          }

                          setFilterField('')
                          setFilterValue('')
                          setFilterValueFrom('')
                          setFilterValueTo('')
                        }}
                      >
                        APPLY FILTER
                      </Button>

                      <Button
                        color='secondary'
                        disabled={
                          !Boolean(
                            parameters.owner ||
                              parameters.vin ||
                              parameters.importerStockNumber ||
                              parameters.year ||
                              parameters.make ||
                              parameters.model
                          )
                        }
                        size='medium'
                        variant='text'
                        onClick={() => {
                          setParameters({
                            ...parameters,
                            owner: undefined,
                            vin: undefined,
                            importerStockNumber: undefined,
                            year: undefined,
                            make: undefined,
                            model: undefined
                          })
                        }}
                      >
                        RESET FILTERS
                      </Button>

                      <Checkbox
                        color='primary'
                        checked={parameters.includeCompletedBundles}
                        onChange={() => {
                          setParameters({ ...parameters, includeCompletedBundles: !parameters.includeCompletedBundles })
                        }}
                      />

                      <Typography variant='body2'>Include Completed Bundles</Typography>
                    </ListFilterGroup>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm='auto' container alignItems='center'>
                  <Grid item>
                    <Button
                      color='primary'
                      onClick={() => {
                        setMichiganSOSBundle(new MichiganSOSBundleDto())
                        setMichiganSOSBundleAddEditDialogOpen(true)
                      }}
                      variant='contained'
                    >
                      ADD BUNDLE
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} container spacing={1} alignItems='center'>
                  {parameters.owner && (
                    <FilterChip
                      label={`Owner contains "${parameters.owner}"`}
                      onDelete={() => setParameters({ ...parameters, owner: undefined })}
                    />
                  )}

                  {parameters.vin && (
                    <FilterChip
                      label={`Last 6 Of VIN contains "${parameters.vin}"`}
                      onDelete={() => setParameters({ ...parameters, vin: undefined })}
                    />
                  )}

                  {parameters.importerStockNumber && (
                    <FilterChip
                      label={`Importer Stock # contains "${parameters.importerStockNumber}"`}
                      onDelete={() => setParameters({ ...parameters, importerStockNumber: undefined })}
                    />
                  )}

                  {parameters.year && (
                    <FilterChip
                      label={`Year equals ${parameters.year}`}
                      onDelete={() => setParameters({ ...parameters, year: undefined })}
                    />
                  )}

                  {parameters.make && (
                    <FilterChip
                      label={`Make contains "${parameters.make}"`}
                      onDelete={() => setParameters({ ...parameters, make: undefined })}
                    />
                  )}

                  {parameters.model && (
                    <FilterChip
                      label={`Model contains "${parameters.model}"`}
                      onDelete={() => setParameters({ ...parameters, model: undefined })}
                    />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {michiganSOSBundles.map(michiganSOSBundle => (
          <Grid item xs={12} key={michiganSOSBundle.michiganSOSBundleGuid}>
            <MichiganSOSBundleListCard michiganSOSBundle={michiganSOSBundle}>
              <Grid item xs={12}>
                <Button
                  endIcon={<Edit />}
                  fullWidth
                  onClick={() => {
                    navigate(`/titles/michigan-sos-bundles/${michiganSOSBundle.michiganSOSBundleGuid}`)
                  }}
                >
                  MANAGE BUNDLE
                </Button>
              </Grid>
            </MichiganSOSBundleListCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <GreenText>
            {`**Michigan Sos Bundles:**

The Michigan SoS Bundles section lists all bundles of title applications being processed by Michigan SoS/DMV. Each bundle contains a list of associated vehicles.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:
   - Owner
   - Last 6 Of VIN
   - Importer Stock #
   - Year
   - Make
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":
   - Owner: Displays a free-type filter field.
   - Last 6 Of VIN: Displays a free-type filter field.
   - Importer Stock #: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
  - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Include Completed Bundles Check Box:**

Unchecked by default

When checked, completed bundles are also displayed.

**Add Bundle Button:**

Clicking Add Bundle opens the Michigan SoS Bundles – Add/Edit MI SoS Bundle page.

**Michigan Bundle Cards:**

Cards on the Michigan SoS Bundles screen are sorted by Drop Off Date (newest to oldest).

- By default, the Include Completed Bundles filter is unchecked.

- A bundle is considered delivered when the Bundle Completed date field is populated.

**Bundle Locked Chip:**

This chip is displayed when a bundle is marked as locked in the Michigan Bundles – Details page.

**Manage Bundle Button:**

Clicking Manage Bundle navigates to the Michigan SoS Bundle Details page for the selected bundle.

**Owner Column:**

Displays the owner’s name from the “Owner Name” field under Vehicle Details > Titles tab.

**Last 6 Of VIN Column:**

Displays the last six characters of the vehicle’s VIN.

**Importer Stock#:**

Displays a sequential number starting at 20000, followed by a three-character Exporter Code or Buyer Code (if the vehicle has a buyer).

This field is populated only when the vehicle is locked.

**Year Column:**

Displays the vehicle’s year from Vehicle Details.

**Make Column:**

Displays the vehicle’s make from Vehicle Details.

**Model Column:**

Displays the vehicle’s model from Vehicle Details.

**Vehicle Check In Date:**

Displays the check in date for when the vehicles check in was completed

**Application Packet Ready For Michigan SoS Bundle:**

Indicates whether the Application Packet is ready for inclusion in a Michigan SoS Bundle.

**Count:**

Displays the total number of vehicles in the bundle.

**Drop Off Date:**

Displays the Drop Off Date, sourced from the MI Title Application Cover Letter – Dialog.

**Branch Location:**

Displays the Michigan SoS Branch Address, sourced from the MI Title Application Cover Letter – Dialog.

**Title Application Delivery Photo Uploaded:**

Displays the date the Title Application Delivery Photo was uploaded, sourced from the Michigan SoS Bundles – Add/Edit MI SoS Bundle page.

**Bundle Completed:**

Displays the date of when the bundle was completed.

**Created:**

Displays the date and user who created the bundle.

**Modified:**

Displays the date and user who most recently modified the bundle.`}
          </GreenText>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => {
              setParameters({
                ...parameters,
                page
              })
            }}
            onRowsPerPageChange={e => {
              setParameters({
                ...parameters,
                pageSize: e.target.value as unknown as number
              })
            }}
            page={parameters.page}
            rowsPerPage={parameters.pageSize}
            labelRowsPerPage={
              <Grid container spacing={1}>
                <Grid item>Order By:</Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, orderBy: e.target.value })
                    }}
                    select
                    size='small'
                    value={parameters.orderBy}
                    variant='standard'
                  >
                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='exporterCompany'>Exporter</MenuItem>

                    <MenuItem value='buyerCompany'>Buyer</MenuItem>

                    <MenuItem value='titleDestination'>Title Destination</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>

                    <MenuItem value='titleUploadedDate'>Title Uploaded Date</MenuItem>

                    <MenuItem value='signedBOLDate'>Signed BOL Date</MenuItem>
                  </TextField>
                </Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                    }}
                    select
                    size='small'
                    value={parameters.order}
                    variant='standard'
                  >
                    <MenuItem value='asc'>Ascending</MenuItem>

                    <MenuItem value='desc'>Descending</MenuItem>
                  </TextField>
                </Grid>

                <Grid item sx={{ alignSelf: 'stretch' }}>
                  <Divider orientation='vertical' />
                </Grid>

                <Grid item>Cards Per Page:</Grid>
              </Grid>
            }
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
